import React, { useEffect, useMemo, useState } from "react";
import { calendarAPI, lessonAPI, unitAPI } from "../services/api";
import ScopeSequenceViewer from "../components/ScopeSequenceViewer";
import "./UnitsPage.css";

const toDateKey = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return raw.split("T")[0];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split("T")[0];
};

const formatDate = (dateValue) => {
  const key = toDateKey(dateValue);
  if (!key) return "TBD";
  const parsed = new Date(`${key}T00:00:00`);
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const durationDays = (startDate, endDate) => {
  const startKey = toDateKey(startDate);
  const endKey = toDateKey(endDate);
  if (!startKey || !endKey) return null;
  const start = new Date(`${startKey}T00:00:00`);
  const end = new Date(`${endKey}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
};

const UnitsPage = ({ courseId }) => {
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedUnitId, setExpandedUnitId] = useState(null);

  useEffect(() => {
    if (!courseId) {
      setUnits([]);
      setLessons([]);
      setCalendarRows([]);
      setExpandedUnitId(null);
      return;
    }

    const loadUnitsView = async () => {
      setLoading(true);
      try {
        const [unitsRes, lessonsRes, calendarRes] = await Promise.all([
          unitAPI.getAll(courseId),
          lessonAPI.getAll({ courseId }),
          calendarAPI.getRange(courseId),
        ]);

        const nextUnits = unitsRes.data || [];
        setUnits(nextUnits);
        setLessons(lessonsRes.data || []);
        setCalendarRows((calendarRes.data || []).map((row) => ({ ...row, _dateKey: toDateKey(row.date) })));

        if (nextUnits.length > 0) {
          setExpandedUnitId((prev) => prev || nextUnits[0].id);
        }
      } catch (error) {
        console.error("Error loading units dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUnitsView();
  }, [courseId]);

  const unitSummaries = useMemo(() => {
    return units.map((unit) => {
      const unitLessons = lessons.filter((lesson) => lesson.unit_id === unit.id);
      const teksCovered = [...new Set(unitLessons.map((lesson) => lesson.teks_code).filter(Boolean))].sort();
      const teksDescriptionsByCode = {};
      unitLessons.forEach((lesson) => {
        if (!lesson.teks_code) return;
        if (!teksDescriptionsByCode[lesson.teks_code] && lesson.teks_description) {
          teksDescriptionsByCode[lesson.teks_code] = lesson.teks_description;
        }
      });
      const chapters = [...new Set(unitLessons.map((lesson) => lesson.chapter_ref).filter(Boolean))].sort();

      const start = toDateKey(unit.start_date);
      const end = toDateKey(unit.end_date);
      const unitCalendar = calendarRows.filter((row) => {
        if (!row._dateKey || !start || !end) return false;
        return row._dateKey >= start && row._dateKey <= end;
      });

      const instructionalDays = unitCalendar.filter((row) => row.lesson_id).length;
      const milestoneDays = unitCalendar.filter((row) => row.milestone).length;
      const cbaDays = unitCalendar.filter((row) => String(row.milestone || "").toUpperCase().includes("CBA")).length;
      const noSchoolDays = unitCalendar.filter((row) => !row.lesson_id && !row.day_type).length;

      return {
        unit,
        unitLessons,
        teksCovered,
        teksDescriptionsByCode,
        chapters,
        instructionalDays,
        milestoneDays,
        cbaDays,
        noSchoolDays,
      };
    });
  }, [calendarRows, lessons, units]);

  const totals = useMemo(() => {
    return unitSummaries.reduce(
      (acc, summary) => {
        acc.lessons += summary.unitLessons.length;
        acc.instructionalDays += summary.instructionalDays;
        acc.milestones += summary.milestoneDays;
        acc.cba += summary.cbaDays;
        return acc;
      },
      { lessons: 0, instructionalDays: 0, milestones: 0, cba: 0 },
    );
  }, [unitSummaries]);

  const sortedUnits = useMemo(
    () =>
      [...units].sort(
        (a, b) => Number(a.unit_number || 0) - Number(b.unit_number || 0),
      ),
    [units],
  );

  const sortedMilestones = useMemo(
    () =>
      calendarRows
        .filter((row) => row._dateKey && row.milestone)
        .sort((a, b) => String(a._dateKey).localeCompare(String(b._dateKey))),
    [calendarRows],
  );

  const chapterMilestones = useMemo(
    () =>
      sortedMilestones.filter((row) =>
        String(row.milestone).toLowerCase().includes("chapter block"),
      ),
    [sortedMilestones],
  );

  const assessmentMilestones = useMemo(
    () =>
      sortedMilestones.filter((row) =>
        /(assessment|cba|finals)/i.test(String(row.milestone)),
      ),
    [sortedMilestones],
  );

  const findUnitForDate = (dateKey) => {
    return sortedUnits.find((unit) => {
      const start = toDateKey(unit.start_date);
      const end = toDateKey(unit.end_date);
      if (!start || !end || !dateKey) return false;
      return dateKey >= start && dateKey <= end;
    });
  };

  const expandedUnit = useMemo(
    () => units.find((unit) => unit.id === expandedUnitId) || null,
    [expandedUnitId, units],
  );

  if (!courseId) {
    return (
      <div className="panel-card">
        <h2>Units</h2>
        <p>Select a course to view units.</p>
      </div>
    );
  }

  return (
    <div className="panel-card units-dashboard" data-tour="units-root">
      <div className="units-header">
        <div>
          <h2>Units Planning View</h2>
          <p>Scope windows, lessons, TEKS coverage, and calendar health by unit.</p>
        </div>
        <div className="units-header-actions">
          <button
            className="primary-btn"
            data-tour="units-open-guide"
            onClick={() => (window.location.hash = "/guide")}
          >
            Open Guide View
          </button>
          <ScopeSequenceViewer
            courseId={courseId}
            selectedUnitNumber={expandedUnit?.unit_number}
          />
        </div>
      </div>

      {loading && <p>Loading units dashboard...</p>}
      {!loading && units.length === 0 && <p>No units found for this course.</p>}

      {!loading && units.length > 0 && (
        <>
          <div className="units-kpis" data-tour="units-kpis">
            <div className="kpi-card">
              <div className="kpi-label">Units</div>
              <div className="kpi-value">{units.length}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Lessons</div>
              <div className="kpi-value">{totals.lessons}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Instructional Days</div>
              <div className="kpi-value">{totals.instructionalDays}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">CBA Milestones</div>
              <div className="kpi-value">{totals.cba}</div>
            </div>
          </div>

          <section className="year-glance" data-tour="units-year-glance">
            <div className="section-title-row">
              <h3>Year at a Glance</h3>
              <p>Built from scope-and-sequence windows and year-at-a-glance milestone markers.</p>
            </div>

            <div className="scope-timeline">
              {sortedUnits.map((unit) => (
                <div key={unit.id} className="scope-card">
                  <div className="scope-label">Unit {unit.unit_number}</div>
                  <h4>{unit.title}</h4>
                  <p>
                    {formatDate(unit.start_date)} - {formatDate(unit.end_date)}
                  </p>
                  <span className="scope-days">
                    {durationDays(unit.start_date, unit.end_date) || "?"} calendar days
                  </span>
                </div>
              ))}
            </div>

            <div className="glance-columns">
              <div className="glance-column">
                <h4>Chapter Pacing Blocks</h4>
                {chapterMilestones.length === 0 && <p className="muted-note">No chapter milestones found.</p>}
                {chapterMilestones.map((row) => {
                  const relatedUnit = findUnitForDate(row._dateKey);
                  return (
                    <div key={`${row._dateKey}-${row.id}`} className="glance-item">
                      <div className="glance-date">{formatDate(row._dateKey)}</div>
                      <div className="glance-text">
                        <strong>{relatedUnit ? `Unit ${relatedUnit.unit_number}` : "Unit"}</strong>
                        <p>{row.milestone}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="glance-column">
                <h4>Assessment and CBA Windows</h4>
                {assessmentMilestones.length === 0 && <p className="muted-note">No assessment milestones found.</p>}
                {assessmentMilestones.map((row) => {
                  const relatedUnit = findUnitForDate(row._dateKey);
                  return (
                    <div key={`${row._dateKey}-${row.id}`} className="glance-item">
                      <div className="glance-date">{formatDate(row._dateKey)}</div>
                      <div className="glance-text">
                        <strong>{relatedUnit ? `Unit ${relatedUnit.unit_number}` : "Assessment"}</strong>
                        <p>{row.milestone}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <div className="units-list" data-tour="units-list">
            {unitSummaries.map((summary) => {
              const {
                unit,
                unitLessons,
                teksCovered,
                teksDescriptionsByCode,
                chapters,
                instructionalDays,
                milestoneDays,
                cbaDays,
                noSchoolDays,
              } = summary;
              const expanded = expandedUnitId === unit.id;

              return (
                <div key={unit.id} className="unit-card">
                  <button
                    type="button"
                    className="unit-card-header"
                    onClick={() => setExpandedUnitId(expanded ? null : unit.id)}
                  >
                    <div>
                      <h3>
                        Unit {unit.unit_number}: {unit.title}
                      </h3>
                      <p>
                        {formatDate(unit.start_date)} to {formatDate(unit.end_date)}
                      </p>
                    </div>
                    <div className="unit-chip-row">
                      <span className="unit-chip">Lessons {unitLessons.length}</span>
                      <span className="unit-chip">TEKS {teksCovered.length}</span>
                      <span className="unit-chip">Days {instructionalDays}</span>
                    </div>
                  </button>

                  {expanded && (
                    <div className="unit-card-body">
                      <p className="unit-rationale">{unit.rationale || "No rationale provided."}</p>

                      <div className="unit-stats-grid">
                        <div><strong>Instructional Days:</strong> {instructionalDays}</div>
                        <div><strong>Milestone Days:</strong> {milestoneDays}</div>
                        <div><strong>CBA Days:</strong> {cbaDays}</div>
                        <div><strong>No-School Days:</strong> {noSchoolDays}</div>
                      </div>

                      <div className="unit-meta-block">
                        <strong>Chapters:</strong>
                        <div className="tag-list">
                          {chapters.length > 0
                            ? chapters.map((chapter) => {
                                const firstChapterLesson = unitLessons.find(
                                  (lesson) => lesson.chapter_ref === chapter,
                                );
                                return (
                                  <button
                                    key={chapter}
                                    type="button"
                                    className="tag tag-btn"
                                    onClick={() =>
                                      firstChapterLesson &&
                                      (window.location.hash = `/lesson/${firstChapterLesson.id}`)
                                    }
                                    disabled={!firstChapterLesson}
                                    title={
                                      firstChapterLesson
                                        ? `Open ${firstChapterLesson.lesson_title}`
                                        : "No lesson found"
                                    }
                                  >
                                    {chapter}
                                  </button>
                                );
                              })
                            : (
                              <span className="tag">None</span>
                            )}
                        </div>
                      </div>

                      <div className="unit-meta-block">
                        <strong>TEKS Covered:</strong>
                        <div className="tag-list">
                          {teksCovered.length > 0
                            ? teksCovered.map((code) => (
                                <span
                                  key={code}
                                  className="tag"
                                  title={
                                    teksDescriptionsByCode[code]
                                      ? `TEKS ${code}: ${teksDescriptionsByCode[code]}`
                                      : `TEKS ${code}`
                                  }
                                >
                                  {code}
                                </span>
                              ))
                            : <span className="tag">None</span>}
                        </div>
                      </div>

                      <div className="unit-actions">
                        <button className="primary-btn" onClick={() => (window.location.hash = "/teks")}>View TEKS Page</button>
                        <button className="primary-btn" onClick={() => (window.location.hash = "/guide")}>View Guide Page</button>
                        {unitLessons[0] && (
                          <button className="primary-btn" onClick={() => (window.location.hash = `/lesson/${unitLessons[0].id}`)}>
                            Open First Lesson
                          </button>
                        )}
                      </div>

                      {unitLessons.length > 0 && (
                        <div className="lesson-preview-list">
                          {unitLessons.slice(0, 6).map((lesson) => (
                            <button
                              key={lesson.id}
                              className="lesson-preview-row"
                              onClick={() => (window.location.hash = `/lesson/${lesson.id}`)}
                              title={
                                lesson.teks_description
                                  ? `TEKS ${lesson.teks_code}: ${lesson.teks_description}`
                                  : `TEKS ${lesson.teks_code || "N/A"}`
                              }
                            >
                              <span>{lesson.lesson_title}</span>
                              <span>TEKS {lesson.teks_code}</span>
                            </button>
                          ))}
                          {unitLessons.length > 6 && (
                            <p className="muted-note">+ {unitLessons.length - 6} more lessons</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default UnitsPage;
